import { Form, FormField, FormSection, FormType } from '../../../../../common/components/reactive-form-builder/interfaces/form.interface';
import { CompradoresEnvironmentDefinition } from '../environment-variables';
import { CompradoresListsDefinition } from './lists-variables';
import { StorageManager } from '../../../../../common/storage-manager.class';
import { InputType } from '../../../../../common/components/reactive-form-builder/interfaces/input.interface';
import { FIRST_CHAR_UNION } from 'src/app/common/components/reactive-form-builder/utils/spanish-id.keyfilter';
import { Compradores } from 'src/app/models/ventas/compradores.model';
import { ReactiveFormValue } from 'src/app/common/components/reactive-form-builder/interfaces/reactive-form-change.interface';

export class CompradoresFormDefinition {
    public formType!: FormType;

    public env: CompradoresEnvironmentDefinition = new CompradoresEnvironmentDefinition();

    public lists: CompradoresListsDefinition = new CompradoresListsDefinition();

    public spanishIdKeyPattern: RegExp = FIRST_CHAR_UNION;
    
    public usuario = StorageManager.getUser();

    public cliente = StorageManager.getClient();

    public model: ReactiveFormValue<Compradores> = {} as ReactiveFormValue<Compradores>;

    public tag = '';

    public formFieldsSection!: FormSection<object>;

    public formFields!: Form<Compradores>;

    public fieldNames: FormField<any>[] = [];

    public fieldLabels: string[] = [];


    constructor(
        type: FormType,
        _model: Record<string, string>,
        table: (string[] | string[][])[] | null
    ) {
        this.formType = +type;

        const action = this.formType === FormType.CREATION ? 'Crear' : 'Actualizar';

        this.formFields = {
            name: 'comercios',
            header: action + ' ' + this.env.comercioName,
            type: this.formType,
            sections: this.getFormSections(),
            table: { title: '', showIndex: false, data: table?.[0], style: table?.[1] },
            uploadButton: {
                isEnabled: false,
                label: ''
            },
            signed: false
        };
    }


    public getFormFields(): FormField<any>[] {
        const formFields: FormField<any>[] = [
            {
                name: 'razon_social',
                vinculatedFields: undefined, 
                label: 'Nombre o Razón Social', 
                input: {type: InputType.TEXT, isEditable: true}, 
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 1
            },
            {
                name: 'nif',
                vinculatedFields: undefined, 
                label: 'NIF / CIF', 
                input: {
                    type: InputType.TEXT, 
                    isEditable: true,
                    maxLength: 9,
                    textConfiguration: {
                        regex: this.spanishIdKeyPattern
                    }
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 2
            },
            {
                name: 'codigo_postal',
                vinculatedFields: undefined, 
                label: 'Código Postal', 
                input: {type: InputType.TEXT, isEditable: true},
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 3
            },
            {
                name: 'poblacion',
                vinculatedFields: undefined, 
                label: 'Población', 
                input: {type: InputType.TEXT, isEditable: true},
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 4
            },
            {
                name: 'provincia', 
                vinculatedFields: undefined,
                label: 'Provincia', 
                input: {type: InputType.TEXT, isEditable: true},
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 5
            },
            {
                name: 'direccion', 
                vinculatedFields: undefined,
                label: 'Dirección', 
                input: {type: InputType.TEXT, isEditable: true},
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'agrosol'
                },
                order: 6
            },
            {
                name: 'pais',
                vinculatedFields: undefined, 
                label: 'País', 
                input: {type: InputType.TEXT, isEditable: true},
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 7
            },
            {
                name: 'rgseaa', 
                vinculatedFields: undefined,
                label: 'RGSEAA', 
                input: {type: InputType.TEXT, isEditable: true},
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 8
            },
            {
                name: 'tipo',
                vinculatedFields: undefined,
                label: 'Tipo',
                input: {type: InputType.DROPDOWN, isEditable: true, data: this.lists.tipos},
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showVentasComercioProveedor
                },
                order: 8
            },
        ];

        this.fieldNames = this.fieldNames.concat(formFields.filter((it) => it.conditions.isVisible));
        this.fieldLabels = this.fieldLabels.concat(formFields.filter((it) => it.conditions.isVisible).map(it => it.label));
        return formFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    public getHiddenFields(): FormField<any>[] {
        const hiddenFields: FormField<any>[] = [
            {
                name: 'id',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.id,
                    isEditable: false
                },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 1
            },
            {
                name: 'id_cliente',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.cliente?.id !== null ? this.cliente?.id : this.lists.fincas.selected?.id_cliente,
                    isEditable: false
                },
                updateValue: () => this.cliente?.id !== null ? this.cliente?.id : this.lists.fincas.selected?.id_cliente,
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.hasClients
                },
                order: 2
            },
            {
                name: 'id_usuario',
                vinculatedFields: undefined,
                label: '',
                input: {
                    isEditable: false,
                    defaultValue: this.env.applicationType === 'cropgest'
                        ? this.cliente?.id_usuario ?? this.usuario.id
                        : this.cliente?.id_usuario_asignado ??
                        this.model.id_usuario ?? this.usuario.id,
                    type: InputType.HIDDEN,
                },
                updateValue: () => this.env.applicationType === 'cropgest'
                    ? this.cliente?.id_usuario ?? this.usuario.id
                    : this.cliente?.id_usuario_asignado ??
                    this.model.id_usuario ?? this.usuario.id,
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 3
            },
        ];

        this.fieldNames = this.fieldNames.concat(hiddenFields.filter((it) => it.conditions.isVisible));
        this.fieldLabels = this.fieldLabels.concat(hiddenFields.filter((it) => it.conditions.isVisible).map(it => it.label));
        return hiddenFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }


    private getFormSections(): FormSection<any>[] {
        const sections: FormSection<any>[] = [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getFormFields(),
                order: 1
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getHiddenFields(),
                order: 2
            },
        ];

        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }
}